import { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Video, Phone, PhoneMissed, PhoneOff, PhoneIncoming, PhoneOutgoing, Check } from 'lucide-react';

/**
 * CallHistoryDialog — per-match log of every call session.
 *
 * Props:
 *   open        controlled open state
 *   onClose     () => void
 *   matchId     match_<xxx>
 *   otherName   display name for the empty state
 *   onCallBack  (mode) => void — invoked when user taps a row's callback icon
 *
 * Backend: GET /api/calls/history/{match_id}
 * Response shape per session:
 *   { invite_id, mode, direction, start_at, end_at, outcome, duration_sec }
 */
function relativeTime(iso) {
  if (!iso) return '';
  const sec = Math.floor((new Date() - new Date(iso)) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatDuration(sec) {
  if (!sec || sec < 1) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

/**
 * Outcome → icon + color mapping. Missed & declined get red; completed
 * gets a check + neutral text; canceled gets a slash phone.
 */
function outcomeConfig(session) {
  const { outcome, direction, mode } = session;
  const CallIcon = mode === 'voice' ? Phone : Video;
  if (outcome === 'missed') {
    return { Icon: PhoneMissed, tint: 'text-[#B91C1C]', label: `Missed ${mode === 'voice' ? 'voice' : 'video'} call` };
  }
  if (outcome === 'declined') {
    return { Icon: PhoneOff, tint: 'text-[#B91C1C]', label: `Declined ${mode === 'voice' ? 'voice' : 'video'} call` };
  }
  if (outcome === 'canceled') {
    return { Icon: PhoneOff, tint: 'text-zinc-500', label: 'Canceled' };
  }
  if (outcome === 'completed') {
    const DirIcon = direction === 'outgoing' ? PhoneOutgoing : PhoneIncoming;
    return { Icon: DirIcon, tint: 'text-emerald-600', label: `${direction === 'outgoing' ? 'Outgoing' : 'Incoming'} ${mode === 'voice' ? 'voice' : 'video'} call` };
  }
  return { Icon: CallIcon, tint: 'text-zinc-500', label: 'Call' };
}

export default function CallHistoryDialog({ open, onClose, matchId, otherName, onCallBack }) {
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (!open || !matchId) return;
    let alive = true;
    setLoading(true);
    api.get(`/calls/history/${matchId}`)
      .then(r => { if (alive) setSessions(r.data.sessions || []); })
      .catch(() => { if (alive) setSessions([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, matchId]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white border-[var(--hairline)] rounded-2xl p-0 max-w-md max-h-[80vh] flex flex-col" data-testid="call-history-dialog">
        <div className="px-5 pt-5 pb-3 border-b border-[var(--hairline)]">
          <DialogTitle className="font-brand text-xl font-[600]">Call history</DialogTitle>
          <p className="text-[12px] text-[#4B4B52] mt-0.5">{otherName ? `with ${otherName}` : 'This match'}</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#B91C1C]" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-16 px-6" data-testid="call-history-empty">
            <div className="w-14 h-14 rounded-full bg-[#F8F9FA] flex items-center justify-center mx-auto mb-3">
              <Phone size={22} strokeWidth={1.8} className="text-zinc-400" />
            </div>
            <p className="text-[14px] font-semibold text-[#0F0F10]">No calls yet</p>
            <p className="text-[12px] text-[#8A8A93] mt-1">
              Start a video or voice call from the chat header.
            </p>
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 divide-y divide-[var(--hairline)]" data-testid="call-history-list">
            {sessions.map((s) => {
              const cfg = outcomeConfig(s);
              const Icon = cfg.Icon;
              const durationText = formatDuration(s.duration_sec);
              return (
                <div
                  key={s.invite_id}
                  data-testid={`call-history-row-${s.invite_id}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-black/[0.02] transition-colors"
                >
                  <div className={`w-9 h-9 rounded-full bg-[#F8F9FA] flex items-center justify-center flex-shrink-0 ${cfg.tint}`}>
                    <Icon size={15} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-[13px] font-semibold truncate ${s.outcome === 'missed' ? 'text-[#B91C1C]' : 'text-[#0F0F10]'}`}>
                      {cfg.label}
                    </p>
                    <p className="text-[11px] text-[#8A8A93] mt-0.5">
                      {relativeTime(s.start_at)}
                      {durationText ? ` \u00b7 ${durationText}` : ''}
                    </p>
                  </div>
                  {onCallBack && (
                    <button
                      onClick={() => { onCallBack(s.mode); onClose(); }}
                      data-testid={`call-history-callback-${s.invite_id}`}
                      aria-label={`Call back with ${s.mode}`}
                      className="w-9 h-9 rounded-full bg-[#B91C1C] hover:bg-[#991B1B] text-white flex items-center justify-center transition-all active:scale-95 flex-shrink-0"
                    >
                      {s.mode === 'voice' ? <Phone size={14} strokeWidth={2.2}/> : <Video size={14} strokeWidth={2.2}/>}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {sessions.length > 0 && (
          <div className="px-5 py-3 border-t border-[var(--hairline)] flex items-center justify-center gap-1.5 text-[11px] text-emerald-700">
            <Check size={11} strokeWidth={2.4} /> End-to-end encrypted via Twilio
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
